import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  Card,
  CardMedia,
  CardContent,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  Divider,
  Alert,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SaveIcon from '@mui/icons-material/Save';
import PreviewIcon from '@mui/icons-material/Preview';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import * as adminService from '../services/adminService';

const PractitionerPhotoManager = ({ clinicId }) => {
  const [practitioners, setPractitioners] = useState([]);
  const [selectedPractitionerId, setSelectedPractitionerId] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'info' });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (clinicId) {
      loadPractitioners();
    }
  }, [clinicId]);

  const loadPractitioners = async () => {
    try {
      setLoading(true);
      if (!clinicId) {
        setError('No clinic selected. Please select a clinic first.');
        setPractitioners([]);
        setLoading(false);
        return;
      }
      
      const response = await adminService.getClinicPractitioners(clinicId);
      setPractitioners(response.data.practitioners || []);
      setError(null);
    } catch (err) {
      console.error('Error loading practitioners:', err);
      setError('Failed to load practitioners. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadPractitionerPhoto = async (practitionerId) => {
    if (!practitionerId) return;
    
    try {
      setLoading(true);
      const response = await adminService.getPractitionerDetails(practitionerId);
      const practitioner = response.data.practitioner;
      setPhotoUrl(practitioner.photo_url || '');
      setError(null);
    } catch (err) {
      console.error('Error loading practitioner photo:', err);
      setError('Failed to load practitioner photo. Please try again.');
      setPhotoUrl('');
    } finally {
      setLoading(false);
    }
  };

  const handlePractitionerChange = (event) => {
    const practitionerId = event.target.value;
    setSelectedPractitionerId(practitionerId);
    if (practitionerId) {
      loadPractitionerPhoto(practitionerId);
    } else {
      setPhotoUrl('');
    }
  };

  const handlePhotoUrlChange = (event) => {
    setPhotoUrl(event.target.value);
  };

  const handleSave = async () => {
    if (!selectedPractitionerId) {
      setAlert({
        open: true,
        message: 'Please select a practitioner first',
        severity: 'warning'
      });
      return;
    }

    try {
      setLoading(true);
      await adminService.updatePractitionerPhoto(selectedPractitionerId, { photo_url: photoUrl });
      
      // Reload practitioner photo to confirm the update
      await loadPractitionerPhoto(selectedPractitionerId);
      
      setAlert({
        open: true,
        message: 'Photo URL updated successfully',
        severity: 'success'
      });
      setError(null);
    } catch (err) {
      console.error('Error saving practitioner photo:', err);
      setAlert({
        open: true,
        message: 'Failed to update photo URL',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewOpen = () => {
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  // Find the selected practitioner's details
  const selectedPractitioner = practitioners.find(p => p.id === selectedPractitionerId);

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h5" gutterBottom>
        Manage Practitioner Photos
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        Update custom photo URLs for practitioners to display in the booking system.
        {!clinicId && " Please select a clinic first from the Clinics page."}
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="practitioner-select-label">Select Practitioner</InputLabel>
            <Select
              labelId="practitioner-select-label"
              value={selectedPractitionerId}
              onChange={handlePractitionerChange}
              label="Select Practitioner"
              disabled={loading || practitioners.length === 0}
            >
              <MenuItem value="">
                <em>-- Select a practitioner --</em>
              </MenuItem>
              {practitioners.map((practitioner) => (
                <MenuItem key={practitioner.id} value={practitioner.id}>
                  {practitioner.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {selectedPractitionerId && (
            <>
              <TextField
                label="Photo URL"
                value={photoUrl}
                onChange={handlePhotoUrlChange}
                fullWidth
                margin="normal"
                disabled={loading}
                placeholder="https://example.com/photo.jpg"
                helperText="Enter the URL for the practitioner's photo. Leave blank to use default Cliniko photo."
              />
              
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Save Photo URL'}
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                  onClick={handlePreviewOpen}
                  disabled={!photoUrl || loading}
                >
                  Preview Photo
                </Button>
              </Box>
            </>
          )}
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper
            elevation={1}
            sx={{
              p: 3,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 300,
              bgcolor: 'background.default'
            }}
          >
            {selectedPractitioner ? (
              <Box sx={{ width: '100%', textAlign: 'center' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Current Photo Preview:
                </Typography>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  {photoUrl ? (
                    <Card elevation={3} sx={{ maxWidth: 300, mb: 2 }}>
                      <CardMedia
                        component="img"
                        image={photoUrl}
                        alt={selectedPractitioner.name}
                        sx={{ 
                          height: 200, 
                          objectFit: 'contain',
                          backgroundColor: 'background.paper'
                        }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/300?text=Invalid+Image+URL';
                        }}
                      />
                      <CardContent sx={{ p: 1, textAlign: 'center' }}>
                        <Typography variant="body2">{selectedPractitioner.name}</Typography>
                      </CardContent>
                    </Card>
                  ) : (
                    <Box sx={{ textAlign: 'center' }}>
                      <PersonIcon sx={{ fontSize: 100, color: 'text.disabled' }} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        No custom photo set - using Cliniko default
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            ) : (
              <Typography variant="body1" color="text.secondary">
                Select a practitioner to manage their photo
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      {/* Photo Preview Dialog */}
      <Dialog open={previewOpen} onClose={handlePreviewClose} maxWidth="md">
        <DialogTitle>
          Photo Preview
          <IconButton
            aria-label="close"
            onClick={handlePreviewClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {photoUrl && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              height: '70vh',
              backgroundColor: 'background.paper'
            }}>
              <img
                src={photoUrl}
                alt="Preview"
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '70vh',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/600x400?text=Invalid+Image+URL';
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePreviewClose}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default PractitionerPhotoManager;